import React, { useMemo, useState, useCallback } from 'react';
import { Shield, ShieldAlert, ShieldCheck, HelpCircle, TrendingUp, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getProfile, getProductEvents, removeProductEvent, saveScores, getDailyLog, saveDailyLog } from '../utils/storage';
import { getToday } from '../utils/dateUtils';
import { computeDailyOverview } from '../scoring/edcScoring';

export default function Dashboard() {
    const navigate = useNavigate();
    const today = getToday();

    const profile = useMemo(() => getProfile() || {}, []);
    const [events, setEvents] = useState(() => getProductEvents(today));
    const [dailyLog, setDailyLog] = useState(() => getDailyLog(today));

    const needsPrompt = !dailyLog || dailyLog.sleepGood === undefined;

    const [promptState, setPromptState] = useState({
        sleepGood: null,
        stressHigh: null,
        exercised: null,
    });

    const overview = useMemo(() => computeDailyOverview(events, profile), [events, profile]);
    const { overallLevel, mainDrivers, mostAffectedAxis, impacts, confidenceSummary } = overview;

    const handleRemoveProduct = useCallback((productName) => {
        // Find and remove the event by product name
        const idx = events.findIndex(e => (e.product?.name || e.product?.product_name) === productName);
        if (idx === -1) return;
        removeProductEvent(today, idx);
        const updated = getProductEvents(today);
        setEvents(updated);
        const newOverview = computeDailyOverview(updated, profile);
        saveScores(today, { ...newOverview, timestamp: new Date().toISOString() });
    }, [events, today, profile]);

    // Badge styling
    let badgeClass = 'signal-unclear';
    let BadgeIcon = HelpCircle;
    if (overallLevel === 'Low Signal') {
        badgeClass = 'signal-low';
        BadgeIcon = ShieldCheck;
    } else if (overallLevel === 'Moderate Signal') {
        badgeClass = 'signal-moderate';
        BadgeIcon = Shield;
    } else if (overallLevel === 'Elevated Signal') {
        badgeClass = 'signal-elevated';
        BadgeIcon = ShieldAlert;
    }

    const hasBehaviors = dailyLog && (dailyLog.sleepGood !== undefined || dailyLog.stressHigh !== undefined || dailyLog.exercised !== undefined);
    const hasData = events.length > 0 || hasBehaviors;

    // Normalize impacts for the horizontal bar width (arbitrary max for visual scale, e.g. 15 points = 100%)
    const maxBarValue = 15;
    const getBarWidth = (val) => `${Math.min(100, (val / maxBarValue) * 100)}%`;

    const handleSavePrompt = () => {
        saveDailyLog(today, promptState);
        setDailyLog(promptState);
        
        // Re-save scores since behavior modifies the final score
        const newOverview = computeDailyOverview(events, profile, promptState);
        saveScores(today, { ...newOverview, timestamp: new Date().toISOString() });
    };

    return (
        <div className="page" style={{ paddingBottom: 100 }}>
            <div className="page-header">
                <h1 className="page-title">Overview</h1>
                <p className="page-subtitle">
                    {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                </p>
            </div>

            {needsPrompt && (
                <div className="overview-hero" style={{ padding: 20, marginBottom: 24 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 16 }}>
                        Daily Check-In
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                        <div>
                            <div style={{ marginBottom: 8, fontSize: 14, fontWeight: 500, color: 'var(--text-secondary)' }}>Did you sleep &gt;7 hours last night?</div>
                            <div className="chip-group">
                                <div className={`chip ${promptState.sleepGood === true ? 'active' : ''}`} onClick={() => setPromptState(p => ({ ...p, sleepGood: true }))}>Yes</div>
                                <div className={`chip ${promptState.sleepGood === false ? 'active' : ''}`} onClick={() => setPromptState(p => ({ ...p, sleepGood: false }))}>No</div>
                            </div>
                        </div>
                        <div>
                            <div style={{ marginBottom: 8, fontSize: 14, fontWeight: 500, color: 'var(--text-secondary)' }}>High stress yesterday?</div>
                            <div className="chip-group">
                                <div className={`chip ${promptState.stressHigh === true ? 'active' : ''}`} onClick={() => setPromptState(p => ({ ...p, stressHigh: true }))}>Yes</div>
                                <div className={`chip ${promptState.stressHigh === false ? 'active' : ''}`} onClick={() => setPromptState(p => ({ ...p, stressHigh: false }))}>No</div>
                            </div>
                        </div>
                        <div>
                            <div style={{ marginBottom: 8, fontSize: 14, fontWeight: 500, color: 'var(--text-secondary)' }}>Did you exercise recently?</div>
                            <div className="chip-group">
                                <div className={`chip ${promptState.exercised === true ? 'active' : ''}`} onClick={() => setPromptState(p => ({ ...p, exercised: true }))}>Yes</div>
                                <div className={`chip ${promptState.exercised === false ? 'active' : ''}`} onClick={() => setPromptState(p => ({ ...p, exercised: false }))}>No</div>
                            </div>
                        </div>
                    </div>
                    {promptState.sleepGood !== null && promptState.stressHigh !== null && promptState.exercised !== null && (
                        <button className="btn btn-primary btn-block" style={{ marginTop: 20 }} onClick={handleSavePrompt}>
                            Log Behaviors
                        </button>
                    )}
                </div>
            )}

            {!hasData ? (
                <div className="card" style={{ textAlign: 'center', marginTop: 16 }}>
                    <div className="empty-state-icon"><HelpCircle size={32} color="var(--text-muted)" /></div>
                    <p style={{ color: 'var(--text-secondary)', fontSize: 14, marginBottom: 16 }}>
                        No products or exposures logged today.
                    </p>
                    <button className="btn-action primary" onClick={() => navigate('/')}>
                        Check a product
                    </button>
                </div>
            ) : (
                <div className="fade-in">

                    {/* Today's Overview */}
                    <div className="overview-hero">
                        <div className="overview-title">Today's Exposure</div>
                        <div className={`signal-badge ${badgeClass} large-badge`}>
                            <BadgeIcon size={18} />
                            <span>{overallLevel}</span>
                        </div>
                        <p className="overview-summary">
                            {overallLevel === 'Low Signal' && "Today's tracked exposures look relatively light."}
                            {overallLevel === 'Moderate Signal' && "A few moderate signals detected in today's routine."}
                            {overallLevel === 'Elevated Signal' && "Today's signals are elevated, mainly driven by personal care items or ultra-processed foods."}
                            {overallLevel === 'Unclear' && "Not enough data to assess today's overall exposure."}
                        </p>
                        <div className={`confidence-text conf-${confidenceSummary}`} style={{ fontSize: 12, marginTop: 12 }}>
                            Confidence: {confidenceSummary} (based on data completeness)
                        </div>
                    </div>

                    {/* Daily Behaviors */}
                    {hasBehaviors && (
                        <div className="overview-section">
                            <h2 className="overview-section-title">Daily Behaviors</h2>
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                {dailyLog.sleepGood === false && (
                                    <div className="chip" style={{ backgroundColor: 'var(--bg-secondary)', color: 'var(--text-secondary)' }}>Poor Sleep</div>
                                )}
                                {dailyLog.stressHigh === true && (
                                    <div className="chip" style={{ backgroundColor: 'var(--bg-secondary)', color: 'var(--text-secondary)' }}>High Stress</div>
                                )}
                                {dailyLog.exercised === true && (
                                    <div className="chip" style={{ backgroundColor: 'rgba(16, 185, 129, 0.1)', color: '#10b981', border: 'none' }}>Exercise</div>
                                )}
                                {dailyLog.sleepGood === true && dailyLog.stressHigh === false && dailyLog.exercised === false && (
                                    <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>No major behavioral impacts logged today.</div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Top Drivers */}
                    {overview.topDrivers && overview.topDrivers.length > 0 && (
                        <div className="overview-section">
                            <h2 className="overview-section-title">Top Drivers Today</h2>
                            <div className="driver-cards">
                                {overview.topDrivers.map((driver, i) => (
                                    <div key={i} className="driver-card">
                                        <div className="driver-card-header">
                                            <div className="driver-card-name">{driver.name}</div>
                                            <button className="driver-remove-btn" onClick={() => handleRemoveProduct(driver.name)} title="Remove product">
                                                <X size={14} />
                                            </button>
                                        </div>
                                        <div className="driver-card-signals">
                                            {driver.signals.map((sig, j) => (
                                                <span key={j} className="driver-signal-tag">{sig}</span>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Axis Breakdown */}
                    {overallLevel !== 'Unclear' && (
                        <div className="overview-section">
                            <h2 className="overview-section-title">Signal Breakdown</h2>
                            <div className="axis-bars">
                                {['metabolic', 'stress', 'reproductive', 'thyroid', 'skin'].map(axis => (
                                    <div key={axis} className="axis-bar-row">
                                        <div className="axis-bar-label">{axis.charAt(0).toUpperCase() + axis.slice(1)}</div>
                                        <div className="axis-bar-track">
                                            <div
                                                className={`axis-bar-fill ${axis}`}
                                                style={{ width: getBarWidth(impacts[axis] || 0) }}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Patterns */}
                    <div className="overview-section">
                        <h2 className="overview-section-title">Observable Patterns</h2>
                        <div className="pattern-card">
                            <TrendingUp size={16} className="pattern-icon" />
                            <p className="pattern-text">
                                {mostAffectedAxis
                                    ? `Most accumulated signals today are interacting with the ${mostAffectedAxis} axis.`
                                    : 'No dominant axis patterns detected today.'}
                            </p>
                        </div>
                    </div>

                    {/* Safer Swaps */}
                    <div className="overview-section">
                        <h2 className="overview-section-title">Safer Swap Opportunities</h2>
                        <div className="swap-card">
                            {mainDrivers.length > 0 ? (
                                (() => {
                                    const topExpl = mainDrivers[0];
                                    const pName = topExpl.split('(')[0].trim();
                                    const sNameRaw = topExpl.includes('(') ? topExpl.split('(').pop().replace(')', '').trim() : '';
                                    const sName = sNameRaw.split(':')[0].trim();
                                    return (
                                        <>
                                            <p className="swap-text">Consider checking out alternatives for: <strong>{pName}</strong>.</p>
                                            <button 
                                                className="btn-action secondary" 
                                                onClick={() => navigate(`/?q=${encodeURIComponent(pName)}&avoid=${encodeURIComponent(sName)}`)} 
                                                style={{ marginTop: 12 }}
                                            >
                                                Search alternatives
                                            </button>
                                        </>
                                    );
                                })()
                            ) : (
                                <p className="swap-text">No immediate swaps suggested.</p>
                            )}
                        </div>
                    </div>

                </div>
            )}

            <div className="detail-disclaimer" style={{ marginTop: 40 }}>
                Signals reflect exposure likelihood based on ingredient proxies. They do not measure actual hormone levels. Not a medical diagnosis.
            </div>
        </div>
    );
}
